import { useRef, useState, createContext, ReactNode, SyntheticEvent, WheelEvent, TouchEvent, UIEvent, RefObject, TouchList, useLayoutEffect, useEffect } from 'react';
import noop from '@picsart/rc/noop';
import View from '@picsart/view/View';

import useBatchData from 'pages/Batch/sections/Context/useBatchData';

import useStyles from './styles';

const MIN_ZOOM = 1;
const MAX_ZOOM = 10;
const ZOOM_STEP = 0.1;
const ZOOM_ACTION_STEP = 0.25;
const IMAGE_MOBILE_SIZE = 350;
const IMAGE_WEB_SIZE = 550;
const MODAL_OFFSET = 24;

const calculatePinchDistance = (touches: TouchList) => {
  const touch1 = touches.item(0);
  const touch2 = touches.item(1);
  const dx = touch1.clientX - touch2.clientX;
  const dy = touch1.clientY - touch2.clientY;
  return Math.sqrt(dx * dx + dy * dy);
};

interface IPreviewModalContext {
  onImageLoad: (event: SyntheticEvent<HTMLImageElement>) => void;
  onWheel: (event: WheelEvent<HTMLDivElement>) => void;
  onTouchStart: (event: TouchEvent<HTMLDivElement>) => void;
  onTouchMove: (event: TouchEvent<HTMLDivElement>) => void;
  onScroll: (event: UIEvent<HTMLDivElement>) => void;
  onTouchEnd: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  isImageLoaded: boolean;
  scrollContainerRef: RefObject<HTMLDivElement>;
  imageRef: RefObject<HTMLImageElement>;
}
const defaultValue: IPreviewModalContext = {
  onImageLoad: noop,
  onWheel: noop,
  onTouchStart: noop,
  onTouchMove: noop,
  onTouchEnd: noop,
  onScroll: noop,
  onZoomIn: noop,
  onZoomOut: noop,
  isImageLoaded: false,
  scrollContainerRef: { current: null },
  imageRef: { current: null },
};

export const PreviewModalContext = createContext<IPreviewModalContext>(defaultValue);

export const PreviewModalContextProvider = ({ children }: {children: ReactNode}) => {
  const [zoom, setZoom] = useState(1);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const initialPinchDistance = useRef<number | null>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const { isWeb } = useBatchData();
  const initialBoxSize = isWeb ? IMAGE_WEB_SIZE : IMAGE_MOBILE_SIZE;
  const [imageBoxSize, setImageBoxSize] = useState(initialBoxSize);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [isImageLoaded, setIsImageLoaded] = useState(defaultValue.isImageLoaded);
  const [imageBoxScrollLeft, setImageBoxScrollLeft] = useState(0);

  const zoomPointPosition = useRef<{x: number; y: number}>();
  const prevScrollContainerSize = useRef<{width: number; height: number}>();

  const classes = useStyles({
    imageBoxSize,
    zoom,
    imageWidth: `${imageSize.width}%`,
    imageHeight: `${imageSize.height}%`,
    imageScale: Math.max(imageSize.width * zoom / 100, 1),
    imageBoxScrollLeft,
  });

  useLayoutEffect(() => {
    const updateImageBoxSize = () => {
      if (modalContentRef.current) {
        const { height, top } = modalContentRef.current.getBoundingClientRect();
        const { clientHeight } = document.body;
        const modalHeightDiff = height + top + MODAL_OFFSET - clientHeight;
        setImageBoxSize(prev => Math.min(prev - modalHeightDiff, initialBoxSize));
      }
    };

    updateImageBoxSize();
    const resizeHandler = () => {
      updateImageBoxSize();
    };

    window.addEventListener('resize', resizeHandler);
    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [initialBoxSize]);

  const handleImageLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    const image = event.currentTarget;
    const imageWidth = image.naturalWidth || 0;
    const imageHeight = image.naturalHeight || 0;
    const coefficient = Math.min(imageBoxSize / imageWidth, imageBoxSize / imageHeight);

    const width = coefficient * imageWidth;
    const height = coefficient * imageHeight;
    setImageSize({
      width: width / imageBoxSize * 100,
      height: height / imageBoxSize * 100,
    });
    setIsImageLoaded(true);
  };

  const handleWheel = (event: WheelEvent<HTMLDivElement>) => {
    if (event.ctrlKey || event.metaKey) {
      if (event.deltaY < 0) {
        setZoom(prevZoom => Math.min(prevZoom + ZOOM_STEP, MAX_ZOOM));
      } else if (event.deltaY > 0) {
        setZoom(prevZoom => Math.max(prevZoom - ZOOM_STEP, MIN_ZOOM));
      }
      zoomPointPosition.current = { x: event.pageX, y: event.pageY };
    }
  };

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    const { touches } = event;
    if (touches.length === 2) {
      initialPinchDistance.current = calculatePinchDistance(touches);
    }
  };

  const handleTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    const { touches } = event;
    if (initialPinchDistance.current && touches.length === 2) {
      const currentPinchDistance = calculatePinchDistance(touches);
      const scale = currentPinchDistance / initialPinchDistance.current;
      setZoom(prevZoom => Math.min(Math.max(prevZoom * scale, MIN_ZOOM), MAX_ZOOM));
      initialPinchDistance.current = currentPinchDistance;
      zoomPointPosition.current = { x: (touches[0].pageX + touches[1].pageX) / 2, y: (touches[0].pageY + touches[1].pageY) / 2 };
    }
  };

  const handleTouchEnd = () => {
    initialPinchDistance.current = null;
  };

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const scrollWidth = event.currentTarget.scrollWidth - event.currentTarget.clientWidth;
    if (scrollWidth === 0) {
      setImageBoxScrollLeft(0);
    } else {
      const scrollPercentage = (event.currentTarget.scrollLeft / scrollWidth) * 100;
      setImageBoxScrollLeft(scrollPercentage);
    }
  };

  const handleZoomIn = () => {
    if (scrollContainerRef.current) {
      const { x, y, width, height } = scrollContainerRef.current.getBoundingClientRect();
      zoomPointPosition.current = { x: x + width / 2, y: y + height / 2 };
    }
    setZoom(prevZoom => Math.min(prevZoom + ZOOM_ACTION_STEP, MAX_ZOOM));
  };

  const handleZoomOut = () => {
    if (scrollContainerRef.current) {
      const { x, y, width, height } = scrollContainerRef.current.getBoundingClientRect();
      zoomPointPosition.current = { x: x + width / 2, y: y + height / 2 };
    }
    setZoom(prevZoom => Math.max(prevZoom - ZOOM_ACTION_STEP, MIN_ZOOM));
  };

  useEffect(() => {
    if (isImageLoaded && scrollContainerRef.current) {
      if (zoomPointPosition.current && prevScrollContainerSize.current) {
        const { x, y } = scrollContainerRef.current.getBoundingClientRect();

        const pointX = zoomPointPosition.current.x - x;
        const pointY = zoomPointPosition.current.y - y;

        const widthDiff = scrollContainerRef.current.scrollWidth / prevScrollContainerSize.current.width;
        const heightDiff = scrollContainerRef.current.scrollHeight / prevScrollContainerSize.current.height;

        const deltaX = (pointX + scrollContainerRef.current.scrollLeft) * (widthDiff - 1);
        const deltaY = (pointY + scrollContainerRef.current.scrollTop) * (heightDiff - 1);
        scrollContainerRef.current.scrollLeft += deltaX;
        scrollContainerRef.current.scrollTop += deltaY;
      }

      prevScrollContainerSize.current = { width: scrollContainerRef.current.scrollWidth, height: scrollContainerRef.current.scrollHeight };
    }
  }, [zoom, isImageLoaded]);

  return (
    <PreviewModalContext.Provider
      value={{
        onImageLoad: handleImageLoad,
        onWheel: handleWheel,
        onTouchStart: handleTouchStart,
        onTouchMove: handleTouchMove,
        onTouchEnd: handleTouchEnd,
        onScroll: handleScroll,
        onZoomIn: handleZoomIn,
        onZoomOut: handleZoomOut,
        isImageLoaded,
        scrollContainerRef,
        imageRef,
      }}
    >
      <View className={classes.content} ref={modalContentRef}>
        {children}
      </View>
    </PreviewModalContext.Provider>
  );
};
